import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	BtnLine,
	BtnTeal,
	CodeBox,
	Gratis,
	PrHead,
	ProfilePage,
	ProfileTabs,
	Sk,
} from '@quizzach/styles';
import { scrollToTop } from '@quizzach/utils';
import { Reveal } from '@quizzach/motion';
import { Navigation, ConfirmationModal } from '@quizzach/components';
import { useMinimumLoadingTime } from '@quizzach/hooks';
import { useAuth, useWindowListener } from '@quizzach/contexts';
import { RoutePath, UserRole } from '@quizzach/interfaces';
import { useUserQuizzes } from '@quizzach/api-hooks';
import { IcCheck, IcClose, IcCopy, IcGear } from '@quizzach/assets';

const MINI_QUIZ_CODE = 'gratis-poveznica';

export const Profile = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const { profileInfo, handleLogout } = useAuth();
	const { width } = useWindowListener();
	const {
		quizzesToList,
		authoredQuizzesToList,
		loading: initialLoading,
	} = useUserQuizzes();
	const quizzesLoading = useMinimumLoadingTime(initialLoading, 300);
	const [showLogoutModal, setShowLogoutModal] = useState(false);
	const [codeCopied, setCodeCopied] = useState(false);
	const watermarkRef = useRef<HTMLSpanElement>(null);

	const currentPath = location.pathname;

	const miniQuizClaimed =
		profileInfo?.used_voucher_codes?.includes(MINI_QUIZ_CODE) ?? false;

	useEffect(() => {
		scrollToTop();
	}, []);

	// The watermark signature scales so the whole nickname spans the header
	// width once: measure at a reference size, then set the fitted size.
	useLayoutEffect(() => {
		const el = watermarkRef.current;
		if (!el) return;
		const fit = () => {
			el.style.fontSize = '100px';
			const scale = el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1;
			// fill-the-header is a desktop gesture; narrow screens cap the
			// signature at handwriting size instead (M4 pick)
			const [minSize, maxSize] = width < 1080 ? [24, 54] : [34, 170];
			el.style.fontSize = `${Math.max(
				minSize,
				Math.min(maxSize, 100 * scale * 0.985)
			)}px`;
		};
		fit();
		document.fonts.ready.then(fit);
	}, [width, profileInfo?.nickname]);

	// label is the short tab wording; title is the full wording the header
	// h1 next to the avatar keeps
	const tabs = [
		{
			path: '/profile/quizzes',
			label: t('profile.tabs.myCollection'),
			title: t('profile.tabs.myCollectionFull'),
			exactMatch: true,
			show: true,
		},
		{
			path: '/profile/transactions',
			label: t('profile.tabs.myPurchases'),
			title: t('profile.tabs.myPurchasesFull'),
			exactMatch: false,
			show: true,
		},
		{
			path: '/profile/author-analytics',
			label: t('profile.tabs.authorAnalytics'),
			title: t('profile.tabs.authorAnalyticsFull'),
			exactMatch: false,
			show: profileInfo?.role === UserRole.ROLE_AUTHOR,
		},
		{
			path: '/profile/settings',
			label: t('profile.tabs.settings'),
			title: t('profile.tabs.settings'),
			exactMatch: false,
			show: false,
		},
	];

	const activeTab =
		tabs.find((tab) =>
			tab.exactMatch ? currentPath === tab.path : currentPath.includes(tab.path)
		) ?? tabs[0];

	const showGratis =
		activeTab.path === '/profile/quizzes' ||
		activeTab.path === '/profile/transactions';

	const handleConfirmLogout = (confirm: boolean) => {
		if (confirm) {
			setShowLogoutModal(false);
			navigate(RoutePath.Login);
			handleLogout();
		} else setShowLogoutModal(false);
	};

	const copyMiniQuizCode = async () => {
		await navigator.clipboard.writeText(MINI_QUIZ_CODE);
		setCodeCopied(true);
		window.setTimeout(() => setCodeCopied(false), 1000);
	};

	return (
		<ProfilePage>
			<ConfirmationModal
				onConfirm={handleConfirmLogout}
				show={showLogoutModal}
				text={t('profile.userInfo.logoutConfirm')}
			/>
			<Navigation />

			<PrHead>
				{profileInfo?.nickname ? (
					<span className='wm' ref={watermarkRef}>
						@{profileInfo.nickname}
					</span>
				) : (
					<Sk className='wm-sk' aria-hidden='true' />
				)}
				<Reveal className='me'>
					<button
						type='button'
						className='big-av'
						onClick={() => navigate(RoutePath.ProfileAvatar)}
						aria-label={t('profile.userInfo.avatarAlt')}
					>
						{profileInfo ? (
							<img
								src={`/avatars/${profileInfo.profile_picture}.webp`}
								alt={t('profile.userInfo.avatarAlt')}
							/>
						) : (
							<Sk className='av-sk' aria-hidden='true' />
						)}
						<span className='edit'>{t('profile.redesign.editAvatar')}</span>
					</button>
					<h1>{activeTab.title}</h1>
				</Reveal>
				<Reveal className='acts' delay={1}>
					<button type='button' onClick={() => navigate('/profile/settings')}>
						<IcGear size={16} />
						{t('profile.tabs.settings')}
					</button>
					<button
						type='button'
						onClick={() => setShowLogoutModal(true)}
						aria-label={t('profile.redesign.logout')}
					>
						<IcClose size={15} />
						{t('profile.redesign.logout')}
					</button>
				</Reveal>
			</PrHead>

			<Reveal>
				<ProfileTabs>
					{tabs.map(
						(tab) =>
							tab.show && (
								<button
									key={tab.path}
									type='button'
									className={activeTab.path === tab.path ? 'on' : ''}
									onClick={() => navigate(tab.path)}
								>
									{tab.label}
								</button>
							)
					)}
				</ProfileTabs>
			</Reveal>

			{/* remount on tab change so the content cross-fades between tabs */}
			<div key={activeTab.path} className='tab-fade'>
				<Outlet
					context={{
						quizzesToList,
						// UserQuizzesOutletContextType names this `loading`; the
						// consumers (UserQuizzes, UserAuthorAnalytics) read it as such
						loading: quizzesLoading,
						authoredQuizzesToList,
					}}
				/>
			</div>

			{showGratis &&
				(miniQuizClaimed ? (
					<Reveal>
						<Gratis>
							<div>
								<span className='hand'>
									{t('profile.discover.titleAccent')}!
								</span>
								<h2>{t('profile.discover.titleRest')}</h2>
								<p>{t('profile.discover.description')}</p>
							</div>
							<div className='gratis-side stack'>
								<BtnTeal
									type='button'
									$small
									onClick={() => navigate(RoutePath.PremadeQuizzes)}
								>
									{t('profile.discover.ctaPremade')}
								</BtnTeal>
								<BtnLine
									type='button'
									$small
									onClick={() => navigate(RoutePath.GeneratorCustomizer)}
								>
									{t('profile.discover.ctaGenerator')}
								</BtnLine>
								<BtnLine
									type='button'
									$small
									onClick={() => navigate(RoutePath.QuickPick)}
								>
									{t('profile.discover.ctaQuick')}
								</BtnLine>
							</div>
						</Gratis>
					</Reveal>
				) : (
					<Reveal>
						<Gratis>
							<div>
								<span className='hand'>
									{t('homepage.gratis.titleAccent')}!
								</span>
								<h2>{t('homepage.gratis.titleRest')}</h2>
								<p>{t('homepage.gratis.description')}</p>
							</div>
							<div className='gratis-side'>
								<CodeBox
									type='button'
									onClick={copyMiniQuizCode}
									aria-label={t('homepage.gratis.copyCodeAriaLabel', {
										code: MINI_QUIZ_CODE,
									})}
								>
									<span className='c'>{MINI_QUIZ_CODE}</span>
									<span className='ic'>
										{codeCopied ? <IcCheck size={20} /> : <IcCopy size={21} />}
									</span>
								</CodeBox>
								<BtnTeal
									type='button'
									$small
									onClick={() => navigate(RoutePath.Voucher)}
								>
									{t('homepage.gratis.cta')}
								</BtnTeal>
							</div>
						</Gratis>
					</Reveal>
				))}
			<div style={{ paddingBottom: '120px' }} />
		</ProfilePage>
	);
};
